import { inject, Injectable } from '@angular/core'

import { sortBy } from 'lodash-es'
import { SelectItem } from 'primeng/api'
import { BehaviorSubject, map, Observable, takeUntil } from 'rxjs'

import { COUNTRY_CODES, TimezoneList } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { Memoize } from '@ti/core/common/decorators'
import { injectDestroy$ } from '@ti/core/common/utils'
import { LanguageService } from '@ti/core/ui-kit/i18n'

@Injectable()
export class CountyOptionsProvider {
  protected readonly api = inject(ApiService)
  protected readonly destroy$ = injectDestroy$()
  protected readonly languageService = inject(LanguageService)
  readonly allCountryOptions$ = new BehaviorSubject<SelectItem[]>([])
  readonly allTimezoneOptions$ = new BehaviorSubject<SelectItem[]>([])

  @Memoize()
  get countryCodeLabels$(): Observable<Record<string, string>> {
    const labelsMap = COUNTRY_CODES.reduce((carry, code) => {
      carry[code] = `countries.${code}`
      return carry
    }, {})
    return this.languageService.massTranslate$(labelsMap)
  }

  @Memoize()
  get countryOptions$(): Observable<SelectItem[]> {
    return this.countryCodeLabels$.pipe(
      takeUntil(this.destroy$),
      map((labels) => {
        const data = COUNTRY_CODES.map((code) => ({ label: labels[code], value: code }))
        const result = sortBy(data, (option) => option.label)
        this.allCountryOptions$.next(result)
        return result
      }),
    )
  }

  @Memoize()
  get timezoneLabels$(): Observable<Record<string, string>> {
    const labelsMap = TimezoneList.reduce(
      (carry, timezone) => {
        const key = this.getTimezoneKey(timezone)
        carry[key] = `timezones.values.${key}.long`
        return carry
      },
      {} as Record<string, string>,
    )
    return this.languageService.massTranslate$(labelsMap)
  }

  protected getTimezoneKey(timezone: string) {
    return timezone
      .split(/[/_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('')
  }
}
